import React, { FC, useCallback, useEffect, useRef, useState } from 'react';
import { Button, Typography } from '@mui/material';
import { useElements, useStripe } from '@stripe/react-stripe-js';
import { StripeAddressElementChangeEvent } from '@stripe/stripe-js';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';

import { CardWrapper, scrollUp } from '@monorepo/common';

import { ArrowRight as ArrowRightIcon } from '../../../../components/Icons/arrow-right';
import { useUpgradeMutation } from '../../../../services/serviceJobData';
import { IEventsStepProps, Step2FormData } from '../../interfaces';
import {
  JobPaymentElements,
  PaymentElementFields,
  confirmJobPayment,
  mapBillingToPayload,
} from '../../../../components/JobPayment';
import { useAppSelector } from '../../../../store/hooks';
import { showNotification } from '../../../../features/sliceNotification';
import { ACCESS_REMOVED_MESSAGE, isForbidden } from '../../../../utils/jobAccessError';
import { formatServerErrors, getResponseErrors } from '../../../../utils/serverStepErrors';

interface IStep2Props extends IEventsStepProps {
  clientSecret: string | null,
  intentError: string | null,
  retryIntent: () => void,
}

interface IStep2PaymentFormProps {
  clientSecret: string | null;
  onBack?: () => void;
  onComplete?: () => void;
  Nav: React.ElementType;
  onBusyChange: (busy: boolean) => void;
}

/**
 * Renders INSIDE <Elements>. Owns the submit sequence: map billing (prefixed) →
 * server validateOnly gate (upgradeJob validateOnly:true) → confirm (skipped when
 * a PI id is memoized for THIS secret) → finalize (upgradeJob validateOnly:false).
 * The finalize forbidden/success/error effects are preserved from the original.
 */
const Step2PaymentForm: FC<IStep2PaymentFormProps> = ({
  clientSecret,
  onBack,
  onComplete,
  Nav,
  onBusyChange,
}) => {
  const stripe = useStripe();
  const elements = useElements();
  const dispatch = useDispatch();
  const { guid } = useParams<{ guid: string }>();

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [finalRequestSent, setFinalRequestSent] = useState<boolean>(false);
  const [addressError, setAddressError] = useState<string | null>(null);
  const [email, setEmail] = useState<string>('');
  const [company, setCompany] = useState<string>('');

  const addressRef = useRef<{ complete: boolean; value: StripeAddressElementChangeEvent['value'] | null }>({
    complete: false,
    value: null,
  });
  const confirmedRef = useRef<{ piId: string; forSecret: string } | null>(null);
  // Single-flight guard (see Step7): held for the whole submit, released in `finally`.
  const finalizeInFlightRef = useRef<boolean>(false);

  const [upgradeJob, { isLoading: isUpgrading, error: finalResponseError, isSuccess: finalResponseSuccess }] = useUpgradeMutation();

  const { siteId } = useAppSelector(({ pathnameSettings }) => ({
    siteId: pathnameSettings.siteNumber.toString()
  }));

  const checkOverallValidity = async (data: Step2FormData): Promise<{ success: boolean, message: string }> => {
    const saveDataResponse = await upgradeJob({
      siteId,
      guid,
      validateOnly: true,
      body: {
        step2: {
          stripePaymentMethodId: '',
          ...data
        }
      }
    });

    if (saveDataResponse && 'error' in saveDataResponse) {
      // Access withdrawn (403) returns a plain-string body — surface the
      // access-removed message instead of trying to parse an errors shape.
      if (isForbidden(saveDataResponse.error)) {
        return { success: false, message: ACCESS_REMOVED_MESSAGE };
      }
      // A keyed FluentValidation `errors` map (Upgrade has no AbstractValidator, so
      // this is defensive) flattens via formatServerErrors.
      if (getResponseErrors(saveDataResponse.error)) {
        return { success: false, message: formatServerErrors(saveDataResponse.error) };
      }
      // The real path: a business-state rejection like "This listing is already
      // featured." arrives as { error }. Surface it verbatim using the same
      // string || .message || .error || generic precedence Reactivate uses. Do NOT
      // route it through formatServerErrors: its generic default last-wins over the
      // correct toast baseQueryWithReAuth already dispatched, hiding the real reason.
      const errBody = (saveDataResponse.error as { data?: unknown }).data;
      const message =
        (typeof errBody === 'string' && errBody)
        || (errBody as { message?: string })?.message
        || (errBody as { error?: string })?.error
        || 'We could not validate this upgrade. Please try again.';
      return { success: false, message };
    }
    return { success: true, message: '' };
  };

  const onSubmit = useCallback(async () => {
    if (!clientSecret) return;
    // Single-flight: return if a submit is already in flight; set the ref BEFORE the
    // first await so a synchronous double-submit collapses to one finalize.
    if (finalizeInFlightRef.current) return;
    finalizeInFlightRef.current = true;
    setIsLoading(true);
    setAddressError(null);

    try {
      // 1. Map billing (prefixed) — incomplete address blocks submit.
      const mapped = mapBillingToPayload({ email, company, address: addressRef.current });
      if (!mapped.ok) {
        setAddressError('Please complete your billing address.');
        return;
      }
      const billing = mapped.prefixed;

      // 2. Server validateOnly gate (UNCHANGED).
      const validation = await checkOverallValidity({ ...billing });
      if (!validation.success) {
        dispatch(showNotification({ show: false, type: 'error', text: validation.message }));
        return;
      }

      // 3. Confirm — reuse the memoized PI id when it matches the current secret.
      let paymentIntentId = confirmedRef.current && confirmedRef.current.forSecret === clientSecret
        ? confirmedRef.current.piId
        : null;

      if (!paymentIntentId) {
        const confirmResult = await confirmJobPayment({ stripe, elements, clientSecret });
        if (confirmResult.error || !confirmResult.paymentIntentId) {
          dispatch(showNotification({
            show: false,
            type: 'error',
            text: confirmResult.error || 'Payment could not be completed. Please try again.',
          }));
          return;
        }
        paymentIntentId = confirmResult.paymentIntentId;
        confirmedRef.current = { piId: paymentIntentId, forSecret: clientSecret };
      }

      // 4. Finalize (UNCHANGED endpoint/payload — stripePaymentMethodId carries the PI
      //    id). Upgrade owns the trigger locally, so AWAIT it directly: busy + the
      //    single-flight guard stay held until the mutation settles, not just dispatch.
      if (guid && siteId) {
        await upgradeJob({
          siteId,
          guid,
          validateOnly: false,
          body: {
            step2: {
              stripePaymentMethodId: paymentIntentId,
              ...billing
            }
          }
        });
        setFinalRequestSent(true);
      }
    } finally {
      finalizeInFlightRef.current = false;
      setIsLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientSecret, email, company, guid, siteId, dispatch, stripe, elements, upgradeJob]);

  useEffect(() => {
    onBusyChange(isLoading || isUpgrading);
  }, [isLoading, isUpgrading, onBusyChange]);

  useEffect(() => {
    if (finalResponseSuccess && onComplete && finalRequestSent) {
      scrollUp();
      onComplete();
    }
  }, [finalResponseSuccess, onComplete, finalRequestSent]);

  useEffect(() => {
    if (isForbidden(finalResponseError)) {
      setIsLoading(false);
      scrollUp();
      dispatch(showNotification({
        show: true,
        type: 'error',
        text: ACCESS_REMOVED_MESSAGE
      }));
      return;
    }
    if (finalResponseError && 'data' in finalResponseError) {
      // Align with checkOverallValidity: a { error } body is the norm (incl. the
      // dup-PI 409 and already-featured refund), but a plain-string or { message }
      // finalize body must not be silent here.
      const errBody = (finalResponseError as { data?: unknown }).data;
      const text =
        (typeof errBody === 'string' && errBody)
        || (errBody as { message?: string })?.message
        || (errBody as { error?: string })?.error
        || 'Internal server error!';
      scrollUp();
      dispatch(showNotification({
        show: true,
        type: 'error',
        text
      }));
      return;
    }
    if (finalResponseError) {
      setIsLoading(false);
      scrollUp();
      dispatch(showNotification({
        show: true,
        type: 'error',
        text: 'Internal server error!'
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [finalResponseError]);

  return (
    <form
      data-cy="step2"
      onSubmit={(e) => {
        e.preventDefault();
        onSubmit();
      }}
    >
      <PaymentElementFields
        showCompany
        company={company}
        onEmailChange={setEmail}
        onCompanyChange={setCompany}
        onAddressChange={(evt) => {
          addressRef.current = { complete: evt.complete, value: evt.value };
          if (evt.complete && addressError) setAddressError(null);
        }}
      />

      {addressError && (
        <Typography
          variant="body2"
          color="error"
          sx={{ mt: 1 }}
        >
          {addressError}
        </Typography>
      )}

      <Nav
        nextDisabled={false}
        onBack={onBack}
        textPreviousStep="Previous step"
        nextButton={(
          <Button
            endIcon={(<ArrowRightIcon fontSize="small" />)}
            data-cy="complete"
            variant="contained"
            disabled={!stripe || isLoading}
            type="submit"
          >
            Continue To Payment
          </Button>
        )}
      />
    </form>
  );
};

export const Step2 = ({ Nav, onBack, onComplete, clientSecret, intentError, retryIntent }: IStep2Props) => {
  const [isBusy, setIsBusy] = useState<boolean>(false);

  return (
    <CardWrapper isLoading={isBusy}>
      <JobPaymentElements
        clientSecret={clientSecret}
        intentError={intentError}
        onRetry={retryIntent}
      >
        <Step2PaymentForm
          clientSecret={clientSecret}
          onBack={onBack}
          onComplete={onComplete}
          Nav={Nav}
          onBusyChange={setIsBusy}
        />
      </JobPaymentElements>
    </CardWrapper>
  );
};
